import { ApptEmailLog } from "@/components/ApptEmailLog";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAppointments, getAssignees, updateAppointment, deleteAppointment, getAdvisors } from "@/lib/apiClient";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { RescheduleButton } from "@/components/RescheduleDialog";
import { ChevronLeft, ChevronRight, CalendarDays, Trash2, Headset, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { APPT_STATUS, typeLabel, modeLabel } from "@/components/AppointmentPanel";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const STATUS_CLS = { gepland: "border-l-[#16BFBF]", afgerond: "border-l-[#001C51] opacity-70", geannuleerd: "border-l-[#B0413E] opacity-50 line-through", no_show: "border-l-[#B7791F] opacity-60" };

function ApptCard({ a, onChanged, showUser }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || a.user_id === user?.id;
  const setStatus = async (status) => {
    try { await updateAppointment(a.id, { status }); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Wijzigen mislukt"); }
  };
  const remove = async () => { if (!window.confirm("Afspraak verwijderen?")) return; await deleteAppointment(a.id); toast.success("Verwijderd"); onChanged(); };
  return (
    <div data-testid={`agenda-appt-${a.id}`} className={`rounded-xl bg-white border border-[#E2E7EC] border-l-4 ${STATUS_CLS[a.status] || ""} p-2.5 text-xs space-y-1`}>
      <p className="font-semibold text-[#001C51]">{a.start.slice(11, 16)} · {a.duration_min} min</p>
      <p className="font-medium text-[#16202B] text-sm leading-tight">{a.lead_name}</p>
      <p className="text-[#5B6675]">{typeLabel(a.type)}{a.mode ? ` · ${modeLabel(a.mode)}` : ""}{showUser ? ` · ${a.user_name}` : ""}</p>
      {a.created_by && a.created_by_id !== a.user_id && <p className="text-[10px] text-[#8994A3]">Geboekt door {a.created_by}</p>}
      {a.note && <p className="text-[#16202B] whitespace-pre-wrap">{a.note}</p>}
      {a.mode === "zoom" && <p className="text-[#5B6675] break-all" data-testid={`agenda-videolink-${a.id}`}>{a.location ? `Videolink: ${a.location}` : "Geen videolink ingesteld"}</p>}
      <ApptEmailLog emails={a.emails} testid={`agenda-emails-${a.id}`} />
      <div className="flex items-center gap-1 pt-1">
        <select data-testid={`agenda-status-${a.id}`} value={a.status} onChange={(e) => setStatus(e.target.value)} className="fe-input py-1 text-xs flex-1 min-w-0">
          {Object.entries(APPT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {a.status === "gepland" && <RescheduleButton appt={a} onDone={onChanged} />}
      </div>
      <div className="flex items-center gap-1">
        <button data-testid={`agenda-open-${a.id}`} onClick={() => navigate(`/belomgeving/${a.lead_id}`)} title="Open in belomgeving" className="p-1.5 rounded-lg hover:bg-[#EDF1F5] text-[#16BFBF]"><Headset className="h-3.5 w-3.5" /></button>
        {canDelete && <button data-testid={`agenda-del-${a.id}`} onClick={remove} title="Verwijderen" className="p-1.5 rounded-lg hover:bg-[#F3DEDD] text-[#B0413E]"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

function AvailabilityTab({ user }) {
  const isAdmin = user.role === "admin";
  const [advisors, setAdvisors] = useState([]);
  const [uid, setUid] = useState(user.is_advisor ? user.id : "");
  useEffect(() => { if (isAdmin) getAdvisors().then((a) => { setAdvisors(a); if (!uid && a.length) setUid(a[0].id); }).catch(() => {}); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#5B6675]">Beschikbaarheid van</span>
          <select data-testid="avail-advisor-select" value={uid} onChange={(e) => setUid(e.target.value)} className="fe-input w-64">
            {advisors.length === 0 && <option value="">Geen adviseurs — zet het vinkje in Teams & gebruikers</option>}
            {advisors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}
      {uid ? <AvailabilityEditor uid={uid} /> : !isAdmin && <p className="text-sm text-[#5B6675]">Je bent geen adviseur.</p>}
    </div>
  );
}

export default function Agenda() {
  const { user } = useAuth();
  const isLead = ["admin", "teamleider"].includes(user?.role);
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [appts, setAppts] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [who, setWho] = useState(isLead ? "" : user.id);
  const canAvail = user?.role === "admin" || user?.is_advisor;
  const [tab, setTab] = useState("agenda");
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  const load = () => getAppointments(ymd(days[0]), ymd(days[6]), who).then(setAppts).catch(() => setAppts([]));
  useEffect(() => { load(); }, [week, who]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isLead) getAssignees().then(setAssignees).catch(() => {}); }, [isLead]);
  const today = ymd(new Date());

  return (
    <div className="px-4 lg:px-8 py-6 max-w-[1600px] mx-auto fe-fade-up" data-testid="agenda-page">
      <header className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Agenda</h1>
          <p className="text-[#5B6675] mt-1 text-sm">Week {days[0].toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} – {days[6].toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        {canAvail && (
          <div className="inline-flex rounded-xl border border-[#E2E7EC] bg-white p-1">
            <button data-testid="agenda-tab-agenda" onClick={() => setTab("agenda")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${tab === "agenda" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><CalendarDays className="h-4 w-4" /> Afspraken</button>
            <button data-testid="agenda-tab-availability" onClick={() => setTab("availability")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${tab === "availability" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Clock className="h-4 w-4" /> {user.role === "admin" ? "Beschikbaarheid" : "Mijn beschikbaarheid"}</button>
          </div>
        )}
        {tab === "agenda" && <div className="ml-auto flex flex-wrap items-center gap-2">
          {isLead && (
            <select data-testid="agenda-user-filter" value={who} onChange={(e) => setWho(e.target.value)} className="fe-input w-52">
              <option value="">{user.role === "admin" ? "Alle medewerkers" : "Heel mijn team"}</option>
              {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <button data-testid="agenda-prev" onClick={() => setWeek(addDays(week, -7))} className="p-2.5 rounded-xl border border-[#E2E7EC] bg-white hover:bg-[#EDF1F5]"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="agenda-today" onClick={() => setWeek(mondayOf(new Date()))} className="px-4 py-2.5 rounded-xl border border-[#E2E7EC] bg-white text-sm font-medium text-[#001C51] hover:bg-[#EDF1F5]">Deze week</button>
          <button data-testid="agenda-next" onClick={() => setWeek(addDays(week, 7))} className="p-2.5 rounded-xl border border-[#E2E7EC] bg-white hover:bg-[#EDF1F5]"><ChevronRight className="h-4 w-4" /></button>
        </div>}
      </header>
      {tab === "availability" ? <AvailabilityTab user={user} /> : <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {days.map((d) => {
          const key = ymd(d);
          const items = appts.filter((a) => a.start.slice(0, 10) === key);
          return (
            <div key={key} data-testid={`agenda-day-${key}`} className={`rounded-2xl border p-3 min-h-[180px] ${key === today ? "border-[#16BFBF] bg-[#EAFBFB]" : "border-[#E2E7EC] bg-[#F6F9FB]"}`}>
              <p className="text-xs uppercase tracking-wide font-semibold text-[#5B6675]">{d.toLocaleDateString("nl-NL", { weekday: "short" })}</p>
              <p className="font-heading font-bold text-xl text-[#16202B] mb-2">{d.getDate()}</p>
              <div className="space-y-2">
                {items.map((a) => <ApptCard key={a.id} a={a} onChanged={load} showUser={isLead && !who} />)}
                {items.length === 0 && <p className="text-xs text-[#8994A3]">—</p>}
              </div>
            </div>
          );
        })}
      </div>
      {appts.length === 0 && (
        <p className="mt-6 text-sm text-[#5B6675] flex items-center gap-2" data-testid="agenda-empty"><CalendarDays className="h-4 w-4" /> Geen afspraken deze week. Plan er een in vanuit de Belomgeving.</p>
      )}
      </>}
    </div>
  );
}
